import { Box, Button, Grid, Modal, Tab, Tabs } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { CalendarInfo } from "./Calendar";
import { panodi } from "../../../networking";
import {
  RHFDateTimePicker,
  RHFTextField,
  RHFTimePicker,
} from "../../RHFComponents/RHFComponents";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AllertMessage } from "../../DoctorsList/Medint/MedintDetails/MedintDetails";

dayjs.extend(customParseFormat);

type StateFormDailyUnavailability = {
  dataInizio: string;
  dataFine: string;
  oraInizio: string;
  oraFine: string;
  note: string;
  aziendaPrenotazione: string;
};

export const DailyUnavailability = (props: {
  calendarInfo: CalendarInfo;
  dataGiornoSelezionato: any;
  setDataGiornoSelezionato: Dispatch<SetStateAction<any>>;
  showOptionInserireIndisponibilita: boolean;
  setShowOptionInserireIndisponibilita: Dispatch<SetStateAction<boolean>>;
  oggettoNotaSelezionato: any;
  onBookingComplete: () => void;
}) => {
  // Allert di conferma/risultato
  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning"
  );
  const [message, setMessage] = useState("");

  // L'alert si chiude dopo 4 secondi se non è di tipo warning
  useEffect(() => {
    if (showAllert && severity !== "warning") {
      const t1 = setTimeout(() => setShowAllert(false), 4000);
      const t2 = setTimeout(() => {
        setSeverity("warning");
        setMessage("");
      }, 4200);
      if (severity === "success") {
        setTimeout(() => {
          props.onBookingComplete();
        }, 4200);
      }
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [severity, showAllert]);

  useEffect(() => {
    if (props.dataGiornoSelezionato) {
      const formattedDate = dayjs(props.dataGiornoSelezionato, [
        "DD/MM/YYYY",
        "YYYY-MM-DD",
      ]).format("YYYY-MM-DD");
      methodsFormDailyUnavailability.setValue("dataInizio", formattedDate);
      methodsFormDailyUnavailability.setValue("dataFine", formattedDate);
    }
  }, [props.dataGiornoSelezionato]);

  const methodsFormDailyUnavailability = useForm<StateFormDailyUnavailability>({
    defaultValues: {
      dataInizio: props?.dataGiornoSelezionato
        ? dayjs(props.dataGiornoSelezionato, "DD/MM/YYYY").format("YYYY-MM-DD")
        : "",
      dataFine: props?.dataGiornoSelezionato
        ? dayjs(props.dataGiornoSelezionato, "DD/MM/YYYY").format("YYYY-MM-DD")
        : "",
      oraInizio: "",
      oraFine: "",
      note: "",
    },
  });

  // funzione send json note giornaliere
  const requestCreateDailyUnavailabilty = async (data: any) => {
    try {
      const payload: any = {
        crud: "C",
        codMedint: props.calendarInfo.codiceMedico,
        dataInizio: data.dataInizio,
        dataFine: data.dataFine,
        oraInizio: data.oraInizio,
        oraFine: data.oraFine,
        descrizione: data.note,
        giorniSelezionati: [true, true, true, true, true, true, false],
        aziendaPrenotazione: props.calendarInfo.aziendaPrenotazione,
      };

      const response: any = await panodi(payload);

      console.log("Risposta dal server:", response);

      if (response.data[0].esito === "OK") {
        setSeverity("success");
      } else {
        setSeverity("error");
      }
      setMessage(response.data[0].messaggio);
    } catch (err) {
      console.error("Errore fetch datiPacada:", err);
    } finally {
      setShowAllert(true);
    }
  };

  const onSubmitFormDailyUnavailability = async (
    data: StateFormDailyUnavailability
  ) => {
    await requestCreateDailyUnavailabilty(data);
  };

  return (
    <Modal open={props.showOptionInserireIndisponibilita}>
      <Box
        sx={{
          maxHeight: "90vh",
          maxWidth: "80vh",
          width: "85%",
          overflow: "auto",
          paddingTop: 2,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 24,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <AllertMessage
          showAllert={showAllert}
          setShowAllert={setShowAllert}
          severity={severity}
          message={message}
        />
        <Tabs value={0} sx={{ paddingLeft: 2, mb: 2 }}>
          <Tab
            sx={{
              fontFamily: "Montserrat",
              fontWeight: "bold",
              textTransform: "none",
            }}
            label="Inserisci Indisponibilità"
          />
        </Tabs>

        <form
          onSubmit={methodsFormDailyUnavailability.handleSubmit(
            onSubmitFormDailyUnavailability
          )}
        >
          <Grid container spacing={2} padding={2} justifyContent="center">
            <Grid item xs={3}>
              <RHFDateTimePicker
                name="dataInizio"
                control={methodsFormDailyUnavailability.control}
                label="Data Inizio"
                dateOnly
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <RHFDateTimePicker
                name="dataFine"
                control={methodsFormDailyUnavailability.control}
                label="Data Fine"
                dateOnly
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <RHFTimePicker
                control={methodsFormDailyUnavailability.control}
                name={`oraInizio`}
                label="Ora Inizio"
                minutesStep={5}
                size="small"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item xs={3}>
              <RHFTimePicker
                control={methodsFormDailyUnavailability.control}
                name={`oraFine`}
                label="Ora Fine"
                minutesStep={5}
                size="small"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <RHFTextField
                control={methodsFormDailyUnavailability.control}
                name="note"
                label="Note"
                placeholder="Note eventuali"
                fullWidth
                required
                multiline
                rows={4}
                inputProps={{ maxLength: 400 }}
                helperText={
                  (methodsFormDailyUnavailability.watch("note")?.length ?? 0) +
                  "/400"
                }
                rules={{
                  required: "Campo obbligatorio",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Button
                  variant="outlinedError"
                  onClick={() => {
                    methodsFormDailyUnavailability.reset();
                    props.setShowOptionInserireIndisponibilita(false);
                  }}
                >
                  Uscita
                </Button>

                <Button type="submit" variant="contained">
                  Conferma
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};
